// src/services/classState.js
// Class state helpers — single source of truth for the `state` lifecycle enum
// introduced by the live-class rebuild (ADR: adr-live-class-rebuild.md, D-051).
//
// Migration note: for one release cycle every write sets BOTH `state` and
// `status` (dual-write). Reads prefer `state` and fall back to `status`. This
// module centralizes the fallback so no consumer reimplements it.
//
// Enum values:
//   draft              — admin created but not saved-for-publishing (reserved)
//   scheduled          — saved and publicly visible in Upcoming
//   waiting_room_open  — within scheduledAt - 15 min window
//   live               — broadcast is active / pre-recorded is playing
//   ending             — admin clicked End Class; 12s drain window in progress
//   ended              — drain complete; thank-you overlay; VOD being processed
//   cancelled          — admin deleted OR cron auto-cancelled at T+15 no-show

// Read the canonical state from a class doc, with fallback to legacy `status`.
// Classes written before the migration only have `status`; new writes have both.
export function getClassState(cls) {
    if (!cls) return null;
    return cls.state || cls.status || null;
}

// Is the class currently live in the viewer-facing sense?
// Includes the drain window ('ending') — viewers are still watching through
// buffered segments and the thank-you overlay hasn't latched yet.
export function isClassLive(cls) {
    const s = getClassState(cls);
    return s === 'live' || s === 'ending';
}

// Is the class in the End-Class drain window? Used to gate UI that should
// stay visible during the graceful end (reactions bar, presence chips) but
// suppress re-entry (no new viewers can join once ending).
export function isClassDraining(cls) {
    return getClassState(cls) === 'ending';
}

// Has the class finished? Thank-you overlay shows from `ended`. Drain-window
// viewers see the overlay when their Mux Player fires `ended` (manifest-seal
// triggered) — tracked separately by the player; this helper is just the
// Firestore terminal-state check.
export function isClassEnded(cls) {
    return getClassState(cls) === 'ended';
}

// Was the class cancelled before it ran?
export function isClassCancelled(cls) {
    return getClassState(cls) === 'cancelled';
}

// Is the class in a "viewer may join the waiting room" state?
// Opens 15 min before scheduledAt (we still expose the pure time-based
// helper `isClassSoon` in liveClasses.js for legacy callers, but new code
// should prefer the explicit state).
export function isWaitingRoomOpen(cls) {
    const s = getClassState(cls);
    return s === 'waiting_room_open';
}
